import React, { useState } from 'react';
import { Field, useField } from 'formik';
import { Icon } from '..';
import { t } from 'i18next';

interface TranslatableFieldProps {
    type?: string;
    as?: string;
    name: string;
    langs?: string[];
    label?: JSX.Element;
    rows?: number;
    placeholder?: string;
    required?: boolean;
    readOnly?: boolean;
    hideEditLink?: boolean;
    inline?: boolean;
    className?: string;
    onChange?: (field: string, value: string) => void;
    onSave?: (field: string, value: string) => void;
    children?: string | JSX.Element | JSX.Element[] | (() => JSX.Element);
};

const TranslatableField = (props: TranslatableFieldProps) => {
    const {
        type, as, name, langs, label, rows, hideEditLink, inline,
        placeholder, required, onChange, onSave, className, readOnly
    } = props;

    const [ isEditing, setIsEditing ] = useState(false);
    const [ field, meta, helpers ]    = useField(name);
    const { initialValue, value }     = meta;
    const { setValue }                = helpers;


    // following should work but for now it returns only ['fr']...
    const languages = langs?.length ? langs : ['fr', 'en', 'de'];
    const [selectedLang, setSelectedLang] = useState(languages[0]);

    const select_lang = (lang: string) => {
        setSelectedLang(lang);
    }

    const _onChange = (lang: string, _value: string) => {
        setValue({
            ...value,
            [lang]: _value
        });
        onChange && onChange(`${name}.${lang}`, _value);
    }

    const save = () => {
        onSave && onSave(`${name}`, value);
    }

    const close = () => {
        setIsEditing(false);
    }

    const handleKeyPress = (e: AnyObj) => {
        e.stopPropagation();
        if (e.key === 'Enter' || e.code === 'Enter') {
            save();
            close();
        } else if (e.key === 'Escape' || e.code === 'Escape') {
            setValue(initialValue);
            close();
        }
    }

    if (readOnly || (!isEditing && !inline))
        return (
            <div className="TranslatableField">
                <div className="mb-1 flex justify-between">
                    {label}
                    {!readOnly && value?.[languages[0]] && !hideEditLink && (
                        <div className="text-xs cursor-pointer font-bold border-none text-gray-500 dark:text-gray-400" onClick={() => setIsEditing(true)}>
                            {t('common.edit')}
                        </div>
                    )}
                </div>
                <div onClick={() => !readOnly && setIsEditing(true)} className="cursor-pointer">
                    {value ? (
                        <div className={`text-gray-700 dark:text-gray-200 rounded leading-6 pt-0.5 p-1.5 ${className}`}>
                            <div dangerouslySetInnerHTML={{ __html: value[languages[0]] }} />
                        </div>
                    ) : (
                        <div className="hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 italic rounded text-sm">
                            <div>{t('changes.title')}</div>
                        </div>
                    )}
                </div>
            </div>
        );

    return (
        <div className="TranslatableField">
            <div>
                {label}
            </div>
            <div className="flex">
                <div className="flex-1">
                    {languages.length && languages.map((l, i) => (
                        <div key={l} className="translatable-field-wrapper" style={{ display: selectedLang === l ? 'initial' : 'none' }}>
                            <Field
                                autoFocus
                                onChange={ (e: AnyObj) => _onChange(l, e.target.value) }
                                onKeyUp={handleKeyPress}
                                as={as || 'input'}
                                type={type || 'text'}
                                name={`${name}.${l}`}
                                rows={rows}
                                id={`name.${l}`}
                                className={`block w-full rounded-md rounded-r-0 border-0 focus:border-0 ${languages.length > 1 ? 'rounded-r-none border-r-0' : ''} p-1.5 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 placeholder:text-gray-400 sm:text-xl sm:leading-6 focus:ring-transparent ${className || ''}`}
                                required={!!required}
                                placeholder={placeholder}
                            />
                        </div>
                    ))}
                </div>
                <div className="flex-none">
                    {languages.length > 1 && (
                        <div className="flex h-full border border-gray-300 dark:border-gray-600 rounded overflow-hidden">
                            <span className={`cursor-pointer font-light flex items-center text-sm px-2 bg-gray-50 dark:bg-gray-900"}`}>
                                <Icon type="globe" />
                            </span>
                            {languages.map((l) => (
                                <span key={l} onClick={(e) => { select_lang(l); return false; }} className={`cursor-pointer font-light flex items-center text-sm px-2 ${selectedLang === l ? "bg-blue-100 dark:bg-blue-900/40" : "bg-gray-50 dark:bg-gray-900"}`}>
                                    {l.toUpperCase()}
                                </span>
                            ))}
                            {!inline && (
                                <span onClick={close} className={"cursor-pointer font-light flex items-center text-sm px-2 bg-white dark:bg-gray-800"}>
                                    <Icon type="close" color="red-600"/>
                                </span>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default TranslatableField;
